import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Package, Truck, CheckCircle2, Loader2, ArrowRight, Download, Lock } from "lucide-react";
import DahliasShell from "../components/dahlias/DahliasShell";
import { generateBoxInvoicePdf } from "../lib/invoicePdf";
import { useSiteText } from "../store/useSiteContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Kraft-paper box artwork supplied by Kayla, keyed by tier slug.
 * Displayed as the visual header of each tier card.
 */
const BOX_ART = {
  beginner: "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/zmelerh2_heritage_boxes_combined_vertical-1%282%29.png",
  hobby:    "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/yivokyn7_heritage_boxes_combined_vertical-1.png",
  addict:   "https://customer-assets.emergentagent.com/job_apparel-dahlias/artifacts/yby04bcj_heritage_boxes_combined_vertical.png",
};

/**
 * Public /dahlias/mystery-boxes , no login required.
 * Customer picks a tier, fills name/email/phone/address + urban|rural,
 * server creates a console_order (kind=public_mystery, stage=awaiting_payment),
 * queues a Heritage HQ invite, and returns bank-deposit details to display.
 */
export default function DahliasMysteryBoxes() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [shipping, setShipping] = useState({ urban: 12, rural: 18 });
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", address: "",
    delivery: "urban", tier: "hobby", notes: "",
    password: "", confirm: "",
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const bannerHeading = useSiteText("mystery.banner.heading", "Mystery Boxes Are Back");
  const bannerBody = useSiteText(
    "mystery.banner.body",
    "A chance to grab an absolute steal from the Heritage Dahlias collection. Once they are gone, they are gone."
  );
  const introHeading = useSiteText("mystery.intro.heading", "Heritage Mystery Boxes");
  const introBody = useSiteText(
    "mystery.intro.body",
    "Our Heritage Mystery Boxes are packed from across the Heritage Dahlias collection and could contain absolutely anything from our catalogue.\n\nFrom customer favourites to collector varieties, every box is different. You will not know exactly what is inside until your box arrives, and that is part of the fun."
  );

  // Pre-fill from /api/customer/me when the visitor is already in HQ.
  useEffect(() => {
    const tk = window.localStorage?.getItem("heritage-customer-token");
    if (!tk) return;
    axios.get(`${API}/customer/me`, { headers: { Authorization: `Bearer ${tk}` } })
      .then((r) => {
        const me = r.data || {};
        setIsLoggedIn(true);
        setForm((f) => ({
          ...f,
          name: me.name || f.name,
          email: me.email || f.email,
          phone: me.phone || f.phone,
          address: typeof me.address === "string" ? me.address : f.address,
        }));
      })
      .catch(() => { /* stale token , ignore, public form takes over */ });
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/public/mystery-boxes`);
        if (cancelled) return;
        setTiers(r.data?.tiers || []);
        setShipping(r.data?.shipping || { urban: 12, rural: 18 });
      } catch {
        if (!cancelled) toast.error("Couldn't load the mystery boxes. Please refresh.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedTier = useMemo(
    () => tiers.find((t) => t.key === form.tier) || tiers[0] || { price: 0, size: 0, label: "", available: 0 },
    [tiers, form.tier],
  );
  const shippingPrice = Number(shipping[form.delivery] || 0);
  const total = Number(selectedTier.price || 0) + shippingPrice;
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim() || !form.address.trim()) {
      toast.error("Please fill every contact field.");
      return;
    }
    const wantsHq = !!form.password;
    if (wantsHq) {
      if (form.password.length < 6) {
        toast.error("Password must be at least 6 characters.");
        return;
      }
      if (form.password !== form.confirm) {
        toast.error("Passwords don't match.");
        return;
      }
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/mystery-boxes/checkout`, {
        ...form,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        password: wantsHq ? form.password : undefined,
      });
      setConfirmation({ ...r.data, tier: selectedTier, delivery: form.delivery });
      // If the server minted an HQ token, drop it into localStorage so the
      // customer is signed in. We don't auto-navigate , the confirmation
      // panel offers an explicit "Open my HQ" CTA after they see the bank
      // deposit details.
      if (r.data?.hq_token) {
        window.localStorage.setItem("heritage-customer-token", r.data.hq_token);
        toast.success("Order placed! You're now signed into Heritage HQ.");
      } else if (r.data?.hq_login_failed) {
        toast.warning("Order placed, but the password didn't match your existing HQ account , please sign in separately.");
      } else {
        toast.success("Order placed! Bank deposit details below.");
      }
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sorry, we couldn't place that order.");
    } finally {
      setSubmitting(false);
    }
  };

  const downloadInvoice = () => {
    if (!confirmation) return;
    const today = new Date();
    const due = new Date(today.getTime() + 14 * 24 * 3600 * 1000);
    const iso = (d) => d.toISOString().slice(0, 10);
    generateBoxInvoicePdf({
      invoiceNumber: confirmation.invoice_number,
      issueDate: iso(today), dueDate: iso(due),
      customer: { name: form.name, email: form.email, phone: form.phone, address: form.address },
      items: [
        { description: `${confirmation.tier.label} (${confirmation.tier.size} surprise heritage tubers)`, qty: 1, unit_price: confirmation.box_price },
        { description: `Shipping (${confirmation.delivery === "urban" ? "Urban" : "Rural"} delivery)`, qty: 1, unit_price: confirmation.shipping },
      ],
      subtotal: confirmation.box_price,
      shipping: confirmation.shipping,
      total: confirmation.total,
      notes: `Direct bank deposit only.  Account: ${confirmation.bank_details.account_name}  ·  ${confirmation.bank_details.bank}  ·  ${confirmation.bank_details.account_no}  ·  Reference: ${confirmation.bank_details.reference}`,
    });
  };

  return (
    <DahliasShell>
      <div className="max-w-6xl mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="public-mystery-page">
        {confirmation ? (
          <Confirmation c={confirmation} onDownload={downloadInvoice} />
        ) : (
          <>
            {/* Banner */}
            <div className="bg-gradient-to-br from-[var(--berry)] to-[var(--berry-deep)] text-[#FBE6D8] wobble-a p-6 md:p-10">
              <div className="font-label tracking-[0.4em] text-[10px] uppercase opacity-90">✦ Limited Numbers ✦</div>
              <h1 className="mt-3 font-display text-4xl md:text-6xl leading-tight">{bannerHeading}</h1>
              <p className="mt-3 text-base md:text-lg max-w-2xl opacity-90 leading-relaxed whitespace-pre-line">
                {bannerBody}
              </p>
            </div>

            {/* Intro */}
            <section className="mt-10 grid grid-cols-1 md:grid-cols-[1.2fr_1fr] gap-8 items-start">
              <div>
                <h2 className="font-display text-3xl md:text-4xl text-[var(--dahlias-forest)] leading-tight">
                  {introHeading}
                </h2>
                <p className="mt-4 text-[var(--dahlias-ink)]/85 leading-relaxed whitespace-pre-line">
                  {introBody}
                </p>
              </div>
              <ul className="space-y-2 text-sm text-[var(--dahlias-ink)]/85">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--berry)]" /> Variety labels included</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--berry)]" /> Direct bank deposit only, no card gateways</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[var(--berry)]" /> Heritage HQ invite included after payment</li>
                <li className="flex items-center gap-2"><Truck className="w-4 h-4 text-[var(--berry)]" /> Urban NZ${shipping.urban} · Rural NZ${shipping.rural}</li>
              </ul>
            </section>

            {/* Tiers */}
            {loading ? (
              <div className="mt-10 grid place-items-center text-[var(--dahlias-ink)]/60 py-12"><Loader2 className="w-5 h-5 animate-spin" /></div>
            ) : (
              <section className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
                {tiers.map((t) => {
                  const active = form.tier === t.key;
                  const soldOut = t.available <= 0;
                  const boxArt = BOX_ART[t.key];
                  return (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => !soldOut && set({ tier: t.key })}
                      disabled={soldOut || !t.active}
                      data-testid={`public-mystery-tier-${t.key}`}
                      className={`text-left wobble-a border-[3px] transition overflow-hidden ${
                        active
                          ? "bg-[var(--dahlias-forest)] text-[#FBE6D8] border-[var(--dahlias-sage-deep)]"
                          : soldOut
                          ? "bg-[var(--dahlias-paper)] text-[var(--dahlias-ink)]/50 border-[var(--dahlias-sage)]/20 cursor-not-allowed grayscale"
                          : "bg-[var(--dahlias-paper)] text-[var(--dahlias-ink)] border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-forest)]/60"
                      }`}
                    >
                      {boxArt && (
                        <div className="aspect-square w-full overflow-hidden bg-[#F3E5C9]">
                          <img
                            src={boxArt}
                            alt={`${t.label} artwork`}
                            loading="lazy"
                            className="w-full h-full object-cover"
                            data-testid={`public-mystery-tier-${t.key}-image`}
                          />
                        </div>
                      )}
                      <div className="p-5">
                        <div className={`font-label tracking-[0.4em] text-[10px] uppercase ${active ? "text-[#FBE6D8]/80" : "text-[var(--berry)]"}`}>
                          {t.label}
                        </div>
                        <div className="mt-2 font-display text-3xl">
                          {t.size} surprise tubers
                        </div>
                        <div className={`mt-1 font-display-italic text-2xl ${active ? "text-[#FBE6D8]" : "text-[var(--berry)]"}`}>
                          NZ${Number(t.price).toFixed(0)} + shipping
                        </div>
                        <div className={`mt-3 text-xs ${active ? "text-[#FBE6D8]/80" : "text-[var(--dahlias-ink)]/65"}`}>
                          {soldOut ? "Sold out" : `${t.available} available`}
                        </div>
                        {active && (
                          <div className="mt-3 inline-flex items-center gap-1.5 text-xs font-label tracking-[0.3em] uppercase">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Selected
                          </div>
                        )}
                      </div>
                    </button>
                  );
                })}
              </section>
            )}

            {/* Checkout form */}
            <form onSubmit={submit} className="mt-10 bg-[var(--dahlias-paper)] border-2 border-[var(--dahlias-sage)]/30 wobble-a p-6 md:p-8 space-y-4" data-testid="public-mystery-form">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <h3 className="font-display text-2xl text-[var(--dahlias-forest)]">{isLoggedIn ? "Confirm your order" : "Place your order"}</h3>
                {!isLoggedIn && (
                  <Link to="/account/login" data-testid="public-signin-link" className="inline-flex items-center gap-1 text-xs font-label tracking-[0.25em] uppercase text-[var(--berry)] hover:text-[var(--berry-bright)]">
                    <Lock className="w-3 h-3" /> Already have HQ? Sign in
                  </Link>
                )}
              </div>
              {isLoggedIn && (
                <div data-testid="public-prefilled-banner" className="bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/30 rounded p-3 text-xs text-[var(--dahlias-forest)] flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[var(--dahlias-sage-deep)]" />
                  Signed into Heritage HQ , your contact details are pre-filled.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FieldGroup label="Full name" required>
                  <input type="text" required value={form.name} onChange={(e) => set({ name: e.target.value })} data-testid="public-name" className="input" placeholder="Jane Smith" />
                </FieldGroup>
                <FieldGroup label="Email" required>
                  <input type="email" required value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="public-email" className="input" placeholder="jane@example.com" />
                </FieldGroup>
                <FieldGroup label="Phone" required>
                  <input type="tel" required value={form.phone} onChange={(e) => set({ phone: e.target.value })} data-testid="public-phone" className="input" placeholder="021 000 000" />
                </FieldGroup>
                <FieldGroup label="Delivery zone" required>
                  <div className="flex gap-2">
                    {[
                      { v: "urban", l: `Urban (NZ$${shipping.urban})` },
                      { v: "rural", l: `Rural (NZ$${shipping.rural})` },
                    ].map((opt) => (
                      <button
                        key={opt.v}
                        type="button"
                        onClick={() => set({ delivery: opt.v })}
                        data-testid={`public-delivery-${opt.v}`}
                        className={`flex-1 px-3 py-2.5 text-xs font-label tracking-[0.25em] uppercase rounded border-2 transition ${
                          form.delivery === opt.v
                            ? "bg-[var(--dahlias-forest)] text-[#FBE6D8] border-[var(--dahlias-sage-deep)]"
                            : "bg-white text-[var(--dahlias-ink)] border-[var(--dahlias-sage)]/30 hover:border-[var(--dahlias-forest)]/60"
                        }`}
                      >
                        {opt.l}
                      </button>
                    ))}
                  </div>
                </FieldGroup>
              </div>
              <FieldGroup label="Shipping address" required>
                <textarea required rows={2} value={form.address} onChange={(e) => set({ address: e.target.value })} data-testid="public-address" className="input resize-none" placeholder="Street, Suburb, City, Postcode" />
              </FieldGroup>
              <FieldGroup label="Notes (optional)">
                <textarea rows={2} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="public-notes" className="input resize-none" placeholder="Anything we should know? (allergies to colours, gift wrap…)" />
              </FieldGroup>

              {/* Optional Heritage HQ password , create or use existing */}
              {!isLoggedIn && (
                <div className="bg-[var(--dahlias-cream)] border border-[var(--dahlias-sage)]/25 rounded p-4 space-y-3" data-testid="public-hq-optin">
                  <div className="flex items-start gap-2">
                    <Lock className="w-4 h-4 text-[var(--berry)] mt-0.5" />
                    <div>
                      <div className="font-display text-base text-[var(--dahlias-forest)]">Create your Heritage HQ login (optional)</div>
                      <div className="text-xs text-[var(--dahlias-ink)]/70 mt-0.5">Track this order, download invoices and shipping updates from your HQ. Skip to checkout as a guest.</div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FieldGroup label="Password (6+ chars)">
                      <input type="password" value={form.password} onChange={(e) => set({ password: e.target.value })}
                        data-testid="public-password" className="input" placeholder="Choose a password" autoComplete="new-password" />
                    </FieldGroup>
                    <FieldGroup label="Confirm password">
                      <input type="password" value={form.confirm} onChange={(e) => set({ confirm: e.target.value })}
                        data-testid="public-password-confirm" className="input" placeholder="Re-enter" autoComplete="new-password" />
                    </FieldGroup>
                  </div>
                </div>
              )}

              {/* Order summary */}
              <div className="bg-white border border-[var(--dahlias-sage)]/30 rounded p-4 space-y-1.5 text-sm">
                <div className="flex justify-between"><span>{selectedTier.label} ({selectedTier.size} tubers)</span><span>NZ${Number(selectedTier.price || 0).toFixed(2)}</span></div>
                <div className="flex justify-between"><span>Shipping ({form.delivery === "urban" ? "Urban" : "Rural"})</span><span>NZ${shippingPrice.toFixed(2)}</span></div>
                <div className="flex justify-between pt-2 border-t border-[var(--dahlias-sage)]/20 font-semibold text-base text-[var(--dahlias-forest)]">
                  <span>Total</span><span data-testid="public-total">NZ${total.toFixed(2)}</span>
                </div>
                <div className="text-[11px] tracking-[0.25em] uppercase text-[var(--berry)] pt-1.5">Direct bank deposit only · invoice emailed after checkout</div>
              </div>

              <div className="pt-2 flex items-center justify-end">
                <button
                  type="submit"
                  disabled={submitting || selectedTier.available <= 0}
                  data-testid="public-place-order"
                  className="inline-flex items-center gap-2 h-12 px-6 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-sm font-label tracking-[0.25em] uppercase disabled:opacity-50"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {submitting ? "Placing order…" : "Place order"}
                </button>
              </div>
            </form>

            <style>{`
              .input { width: 100%; padding: 0.6rem 0.75rem; background: white; border: 1px solid rgba(135, 150, 107, 0.3); border-radius: 0.375rem; font-size: 0.875rem; outline: none; }
              .input:focus { border-color: #6E1E3A; }
            `}</style>
          </>
        )}
      </div>
    </DahliasShell>
  );
}

function Confirmation({ c, onDownload }) {
  return (
    <div className="bg-[var(--dahlias-paper)] border-2 border-[var(--berry)]/30 wobble-a p-7 md:p-10" data-testid="public-mystery-confirmation">
      <div className="font-label tracking-[0.4em] text-[10px] uppercase text-[var(--berry)]">✦ Order Placed ✦</div>
      <h1 className="mt-2 font-display text-4xl md:text-5xl text-[var(--dahlias-forest)] leading-tight">
        Bank deposit time
      </h1>
      <p className="mt-3 text-base text-[var(--dahlias-ink)]/85 max-w-2xl">
        Your <strong>{c.tier?.label}</strong> ({c.tier?.size} tubers) is reserved. Pay <strong>NZ${Number(c.total).toFixed(2)}</strong> via direct bank deposit and we&apos;ll lock it in.
      </p>

      <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-[var(--dahlias-sage)]/30 rounded p-4">
          <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--berry)]">Bank deposit details</div>
          <dl className="mt-3 space-y-1.5 text-sm">
            <Row k="Account name" v={c.bank_details.account_name} testid="bank-account-name" />
            <Row k="Bank" v={c.bank_details.bank} testid="bank-name" />
            <Row k="Account no." v={c.bank_details.account_no} testid="bank-account" />
            <Row k="Reference" v={c.bank_details.reference} highlight testid="bank-reference" />
            <Row k="Amount" v={`NZ$${Number(c.total).toFixed(2)}`} highlight testid="bank-amount" />
          </dl>
        </div>
        <div className="bg-[var(--berry)]/8 border border-[var(--berry)]/30 rounded p-4">
          <div className="font-label tracking-[0.3em] text-[10px] uppercase text-[var(--berry)]">What happens next</div>
          <ol className="mt-3 space-y-2 text-sm text-[var(--dahlias-ink)]/85 list-decimal pl-4">
            {(c.next_steps || []).map((step, i) => <li key={i}>{step}</li>)}
          </ol>
        </div>
      </section>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <button
          type="button" onClick={onDownload}
          data-testid="public-download-invoice"
          className="inline-flex items-center gap-2 h-11 px-5 wobble-pill bg-[var(--berry)] hover:bg-[var(--berry-deep)] text-white text-xs font-label tracking-[0.25em] uppercase"
        >
          <Download className="w-3.5 h-3.5" /> Download invoice
        </button>
        {c.hq_token ? (
          <a href="/account" data-testid="public-open-hq" className="inline-flex items-center gap-2 h-11 px-5 bg-[var(--dahlias-forest)] hover:bg-[var(--dahlias-sage-deep)] text-[#FBE6D8] text-xs font-label tracking-[0.25em] uppercase rounded">
            <Lock className="w-3.5 h-3.5" /> Open my Heritage HQ <ArrowRight className="w-3 h-3" />
          </a>
        ) : (
          <a href="/dahlias" className="inline-flex items-center gap-1.5 text-xs font-label tracking-[0.25em] uppercase text-[var(--dahlias-forest)] hover:underline">
            Back to Heritage Dahlias <ArrowRight className="w-3 h-3" />
          </a>
        )}
      </div>

      <p className="mt-6 text-xs text-[var(--dahlias-ink)]/60">
        Order #{c.order_id.slice(0, 8)} · Invoice {c.invoice_number}.{" "}
        {c.is_new_customer && "A Heritage HQ invitation will be sent once payment is confirmed."}
      </p>
    </div>
  );
}

function FieldGroup({ label, required, children }) {
  return (
    <div>
      <div className="font-label tracking-[0.25em] text-[10px] uppercase text-[var(--dahlias-sage-deep)] mb-1.5">
        {label} {required && <span className="text-[var(--berry)]">*</span>}
      </div>
      {children}
    </div>
  );
}

function Row({ k, v, highlight, testid }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <dt className="text-[10px] tracking-[0.25em] uppercase text-[var(--dahlias-ink)]/60">{k}</dt>
      <dd data-testid={testid} className={`text-sm font-mono ${highlight ? "text-[var(--berry-deep)] font-semibold" : "text-[var(--dahlias-ink)]"}`}>{v}</dd>
    </div>
  );
}
